import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Sidebar } from 'primereact/sidebar';
import Loader from "../../Shared/Loader/Loader";
import APIConstant from "../../../utils/pathConstants";
import api from "../../../utils/apiClient";
import CommonConfig from '../../../utils/constant';
import AddEditFeeTemplate from './AddEditFeeTemplate';
import moment from 'moment';
import Toast from '../../Shared/Toast/Toast';
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const FeeTemplateConfiguration = () => {
    const { catchResponse } = useCatchResponse();
    const [templateList, setTemplateList] = useState([]);
    const [masterData, setMasterData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [securityUserID, setSecurityUserID] = useState("");
    const [rows, setRows] = useState(10);
    const [first, setFirst] = useState(0);
    const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const [pageAccess, setPageAccess] = useState({});

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        const accessData = CommonConfig.getUserAccess('Fee Template');
        setPageAccess(accessData);
        GetFeeTemplateList(loginData?._id);
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const onPageChange = (e) => {
        setFirst(e.first);
        setRows(e.rows);
    };

    const GetFeeTemplateList = async (id) => {
        try {
            let data = { UserID: id };
            await api.post(APIConstant.path.GetFeeTemplateList, data).then(async response => {
                if (response.success) {
                    setTemplateList(response.data);
                    Loader.hide();
                }
            }).catch(err => {
                Loader.hide();
                catchResponse(err);
            });
        } catch (err) {
            catchResponse(err);
        }
    };

    const actionBodyTemplate = (rows) => {
        return (
            <div className='p-d-flex p-ai-center p-jc-end'>
                {pageAccess?.EditAccess && (
                    <Button icon="pi pi-pencil" className="e-sction-btn p-my-0 p-m-0" onClick={() => {
                        setMasterData(rows);
                        setVisible(true);
                    }} />
                )}
                {pageAccess?.DeleteAccess && (
                    <Button icon="pi pi-trash" className="e-sction-btn p-my-0 p-mr-0 p-ml-2" onClick={() => {
                        setIsDeleteConfirmation(true);
                        setDeleteId(rows._id);
                    }} />
                )}
            </div>
        );
    };

    const lastUpdatedBodyTemplate = (rows) => {
        return <div>{moment(rows?.UpdatedOn || rows?.CreatedOn).format("DD-MM-YYYY HH:mm")}</div>;
    };

    const onCancel = () => {
        setVisible(false);
        setMasterData([]);
        GetFeeTemplateList(securityUserID);
    };

    const actionBodyTemplateSrNo = (data, props) => {
        return props.rowIndex + 1;
    };

    const handleClose = () => {
        setIsDeleteConfirmation(false);
    };

    const DeleteTemplate = async () => {
        try {
            let data = { id: deleteId };
            await api.post(APIConstant.path.DeleteFeeTemplate, data).then(async response => {
                if (response.success) {
                    Loader.hide();
                    Toast.success({ message: 'Fee Template deleted successfully' });
                    setDeleteId('');
                    GetFeeTemplateList(securityUserID);
                }
                handleClose();
            }).catch(err => {
                Loader.hide();
                setDeleteId('');
                handleClose();
                catchResponse(err);
            });
        } catch (err) {
            catchResponse(err);
        }
    };

    const renderFooter = () => (
        <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
            <Button label="Cancel" className="btn-text-action-secondary" onClick={handleClose} />
            <Button label="Delete" className="btn-text-action-primary" onClick={() => DeleteTemplate()} />
        </div>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-file"></i>
                    <h2>Fee Template</h2>
                </div>
                <div className="p-d-flex p-ai-center p-jc-between">
                    <div className="searchbar-area e-searchbar-area">
                        <i className="pi pi-search" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Global Search" />
                    </div>
                    {pageAccess?.AddAccess && (
                        <Button icon="pi pi-plus" className="e-h-btn" onClick={() => setVisible(true)} />
                    )}
                </div>
            </div>
            <DataTable
                value={templateList}
                paginator
                rows={rows}
                first={first}
                filters={filters}
                filterDisplay="menu"
                responsiveLayout="scroll"
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
                rowsPerPageOptions={[5, 10, 20]}
                currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
                onPage={onPageChange}
                className='custom-table'
                scrollHeight='calc(100vh - 120px)'
                size='small'
            >
                <Column field="id" body={actionBodyTemplateSrNo} header="Sr No." style={{ width: '15%' }} />
                <Column field="TemplateName" header="Template Name" style={{ width: '35%' }} />
                <Column field="ReceiptTitle" header="Receipt Title" style={{ width: '25%' }} />
                <Column body={lastUpdatedBodyTemplate} header="Last Updated On" style={{ width: '25%' }} />
                <Column body={actionBodyTemplate} header="Action" headerStyle={{ width: '100px', textAlign: 'center' }} />
            </DataTable>

            <Sidebar
                header={masterData?._id ? 'Edit Fee Template' : 'Add Fee Template'}
                className='dialog-popup'
                visible={visible}
                onHide={() => onCancel()}
                draggable={false}
                position="right"
                contentStyle={{ overflow: 'hidden' }}
            >
                <AddEditFeeTemplate onCancel={onCancel} configId={masterData?._id} masterData={masterData} />
            </Sidebar>

            <Dialog
                visible={isDeleteConfirmation}
                onHide={handleClose}
                header={<div className="p-d-flex p-jc-between p-ai-center"><span>Delete Confirmation</span></div>}
                footer={renderFooter()}
                draggable={false}
                dismissableMask
                position="center"
                style={{ width: '450px' }}
            >
                <DeleteConfirmation />
            </Dialog>
        </div>
    );
};

export default FeeTemplateConfiguration;
